import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export type FeedbackType = 'bug' | 'suggestion' | 'question' | 'other';

export interface FeedbackData {
  type: FeedbackType;
  subject: string;
  message: string;
  screenshot?: File;
}

export function useFeedback() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user, establishmentId } = useAuth();

  const submitFeedback = async (data: FeedbackData): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour envoyer un feedback",
        variant: "destructive",
      });
      return false;
    }

    setIsSubmitting(true);
    try {
      let screenshotPath: string | null = null;

      // Upload screenshot if provided
      if (data.screenshot) {
        const fileExt = data.screenshot.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('feedback-screenshots')
          .upload(fileName, data.screenshot);

        if (uploadError) {
          console.error('Screenshot upload error:', uploadError);
          // Continue without screenshot
        } else {
          screenshotPath = fileName;
        }
      }

      // Collect browser info
      const browserInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      };

      // Insert feedback
      const { error } = await supabase
        .from('feedbacks')
        .insert({
          user_id: user.id,
          user_email: user.email || '',
          user_name: user.user_metadata?.full_name || user.email?.split('@')[0] || '',
          establishment_id: establishmentId,
          type: data.type,
          subject: data.subject,
          message: data.message,
          screenshot_path: screenshotPath,
          page_url: window.location.href,
          browser_info: browserInfo,
        });

      if (error) throw error;

      // Send email notification (fire and forget - don't block user)
      supabase.functions.invoke('send-feedback-notification', {
        body: {
          type: data.type,
          subject: data.subject,
          message: data.message,
          userEmail: user.email,
          userName: user.user_metadata?.full_name || user.email?.split('@')[0] || '',
          establishmentId: establishmentId,
          pageUrl: window.location.href,
          screenshotPath: screenshotPath,
        }
      }).catch(err => console.error('Feedback notification error:', err));

      toast({
        title: "Merci pour votre retour !",
        description: "Votre feedback a été envoyé avec succès. Nous le traiterons rapidement.",
      });

      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer votre feedback. Veuillez réessayer.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitFeedback, isSubmitting };
}
